import React, { useEffect, useState } from 'react';
import { Col, Row, Container } from 'react-bootstrap';
import { Typewriter } from 'react-simple-typewriter';
import { FaPalette, FaStore, FaArrowRight, FaPaintBrush, FaShoppingBag, FaStar, FaHeart, FaUsers } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Logo from '../../../assets/logo7.png';
import './modeselection.scss';
import withAuth from '../../auth/WithAuth';

const ModeSelection = () => {
  const navigate = useNavigate();
  const [selectedMode, setSelectedMode] = useState(null);
  const [userName, setUserName] = useState('');
  const [userAvatar, setUserAvatar] = useState('');

  // Get user info from localStorage
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const role = localStorage.getItem('role');
    const savedMode = localStorage.getItem('user_mode');
    const name = localStorage.getItem('full_name');
    
    setUserName(name.split(' ')[0]);
    
    // Generate avatar initials
    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase();
    setUserAvatar(initials);

    // If user is already logged in and has a mode saved, redirect them
    if (token && role) {
      if (role === 'artisan') {
        navigate('/artisan-dashboard');
      } else if (role === 'admin') {
        navigate('/admin-dashboard');
      } else {
        if (savedMode) {
          if (savedMode === 'custom') {
            navigate('/client-dashboard');
          } else if (savedMode === 'shop') {
            navigate('/ready-made-products');
          }
        }
      }
    } else {
      // If not logged in, redirect to login
      navigate('/login');
    }
  }, [navigate]);

  // Handle mode selection and proceed
  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
  };

  const handleProceed = () => {
    if (!selectedMode) {
      toast.warning('Please select how you would like to proceed');
      return;
    }

    // Save the selected mode
    localStorage.setItem('user_mode', selectedMode);

    // Show success message
    toast.success(`Taking you to ${selectedMode === 'custom' ? 'Custom Orders' : 'Ready-Made Shop'}!`);

    // Navigate based on selection
    setTimeout(() => {
      if (selectedMode === 'custom') {
        navigate('/client-dashboard');
      } else {
        navigate('/ready-made-products');
      }
    }, 1500);
  };

  return (
    <div className="mode-selection-page">
      {/* Decorative Background Elements */}
      <div className="bg-decoration">
        <div className="circle circle-1"></div>
        <div className="circle circle-2"></div>
        <div className="circle circle-3"></div>
        <div className="circle circle-4"></div>
      </div>

      <Container fluid className="h-100">
        <Row className="h-100 g-0">
          {/* Left Side - Branding & Welcome */}
          <Col lg={5} className="brand-section">
            <div className="brand-content">
              {/* Logo */}
              <Link to="/" className="logo-link">
                <img src={Logo} alt="Artiza Logo" className="brand-logo" />
              </Link>

              {/* User Welcome Card */}
              <div className="welcome-card">
                <div className="user-avatar">
                  {userAvatar}
                </div>
                <div className="welcome-text">
                  <span className="greeting">Welcome back,</span>
                  <h2 className="user-name">{userName}.</h2>
                </div>
              </div>

              {/* Animated Quote */}
              <div className="quote-container">
                <div className="quote-icon">"</div>
                <div className="typewriter-wrapper">
                  <Typewriter
                    words={[
                      
                      'Creativity takes courage.',
                      'Art is not what you see, but what you make others see.',
                      'Inspiration exists, but it has to find you working.',
                    ]}
                    loop={true}
                    cursor
                    cursorStyle="|"
                    typeSpeed={50}
                    deleteSpeed={30}
                    delaySpeed={2000}
                  />
                </div>
              </div>

              {/* Stats Cards */}
              <div className="stats-container">
                <div className="stat-card">
                  <div className="stat-icon">
                    <FaUsers />
                  </div>
                  <div className="stat-info">
                    <span className="stat-value">500+</span>
                    <span className="stat-label">Artisans</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <FaStar />
                  </div>
                  <div className="stat-info">
                    <span className="stat-value">1000+</span>
                    <span className="stat-label">Projects</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <FaHeart />
                  </div>
                  <div className="stat-info">
                    <span className="stat-value">98%</span>
                    <span className="stat-label">Satisfaction</span>
                  </div>
                </div>
              </div>

              {/* Bottom Decoration */}
              {/* <div className="brand-footer">
                <div className="brand-tagline">
                  Where creativity meets craftsmanship
                </div>
              </div> */}
            </div>
          </Col>

          {/* Right Side - Mode Selection Cards */}
          <Col lg={7} className="selection-section">
            <div className="selection-container">
              <div className="selection-header">
                {/* <h2 className="selection-title">Choose Your Path</h2> */}
                <p className="selection-subtitle">How would you like to experience Artiza today?</p>
              </div>

              <div className="mode-cards">
                {/* Custom Orders Card */}
                <div
                  className={`mode-card ${selectedMode === 'custom' ? 'selected' : ''}`}
                  onClick={() => handleModeSelect('custom')}
                >
                  <div className="card-badge">Most Popular</div>
                  <div className="card-bg-pattern"></div>
                  <div className="card-icon-wrapper">
                    <FaPalette className="card-icon" />
                  </div>
                  <h3 className="card-title">Custom Orders</h3>
                  <p className="card-description">
                    Have something specific in mind? Describe your vision and let artisans 
                    bring it to life.
                  </p>
                  <div className="card-features">
                    <div className="feature-item">
                      <span className="feature-check">✓</span>
                      <span>Personalized designs</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-check">✓</span>
                      <span>Negotiate pricing</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-check">✓</span>
                      <span>Track progress</span>
                    </div>
                  </div>
                  {selectedMode === 'custom' && (
                    <div className="selected-indicator">
                      <FaPaintBrush />
                      <span>Selected</span>
                    </div>
                  )}
                </div>

                {/* Ready-Made Shop Card */}
                <div
                  className={`mode-card shop-card ${selectedMode === 'shop' ? 'selected' : ''}`}
                  onClick={() => handleModeSelect('shop')}
                >
                  <div className="card-badge">Quick & Easy</div>
                  <div className="card-bg-pattern"></div>
                  <div className="card-icon-wrapper">
                    <FaStore className="card-icon" />
                  </div>
                  <h3 className="card-title">Ready-Made Shop</h3>
                  <p className="card-description">
                    Browse and purchase existing artwork and products immediately. 
                    No waiting - just add to cart and checkout!
                  </p>
                  <div className="card-features">
                    <div className="feature-item">
                      <span className="feature-check">✓</span>
                      <span>Instant purchase</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-check">✓</span>
                      <span>Fast shipping</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-check">✓</span>
                      <span>Secure checkout</span>
                    </div>
                  </div>
                  {selectedMode === 'shop' && (
                    <div className="selected-indicator">
                      <FaShoppingBag />
                      <span>Selected</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Proceed Button */}
              <button
                className={`proceed-btn ${selectedMode ? 'active' : ''}`}
                onClick={handleProceed}
                disabled={!selectedMode}
              >
                <span>Continue to {selectedMode === 'custom' ? 'Custom Orders' : selectedMode === 'shop' ? 'Shop' : 'Your Selection'}</span>
                <FaArrowRight className="btn-icon" />
              </button>

            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};


export default withAuth(ModeSelection, 'client');